import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useCurrentProvider } from '../hooks/useProvider';
import { authApi } from '../services/api';
import { uploadProfileImage } from '../services/firebase';
import { AxiosError } from 'axios';
import cloutLogo from '../assets/logos/clout-logo.png';
import {
  Step1BasicInfo,
  Step2Professional,
  Step3Services,
  Step4Education,
  Step5Practice,
  Step6Review,
} from '../components/wizard';

interface ProfileData {
  // Basic Info
  fullName: string;
  phone: string;
  profileImage: File | null;
  profileImageUrl?: string;

  // Professional Info
  businessName: string;
  licenseNumber: string;
  yearsOfExperience: number;
  bio: string;

  // Location
  address: string;
  city: string;
  state: string;
  zipCode: string;
  website: string;

  // Services (merged from specialties and treatmentCategories)
  specialties: string[]; // Legacy field for backward compatibility
  services: string[];
  treatmentCategories: string[]; // Legacy field for backward compatibility
  pricingRange: string;
  virtualConsultations: boolean;

  // Education & Credentials
  education: Array<{
    degree: string[];
    institution: string;
    year: string;
  }>;
  certifications: Array<{
    name: string[];
    issuer: string;
    year: string;
  }>;
  affiliations: string[];

  // Additional Info
  insuranceAccepted: string[];
  languagesSpoken: string[];
}

const ProfileWizard: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { provider: existingProvider } = useCurrentProvider();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Clear any pending post-save redirect on unmount
  useEffect(() => () => clearTimeout(redirectTimerRef.current), []);

  const [profileData, setProfileData] = useState<ProfileData>({
    fullName: '',
    phone: '',
    profileImage: null,
    businessName: '',
    licenseNumber: '',
    yearsOfExperience: 0,
    bio: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    website: '',
    specialties: [],
    services: [],
    treatmentCategories: [],
    pricingRange: '',
    virtualConsultations: false,
    education: [],
    certifications: [],
    affiliations: [],
    insuranceAccepted: [],
    // No step asks for languages, so don't assume any
    languagesSpoken: [],
  });

  const totalSteps = 6;
  const steps = [
    'Basic Information',
    'Professional Details',
    'Services & Specialties',
    'Education & Credentials',
    'Practice Information',
    'Review & Submit',
  ];

  useEffect(() => {
    // Pre-fill with existing provider data if available
    if (existingProvider) {
      setProfileData((prev) => ({
        ...prev,
        fullName: currentUser?.full_name || '',
        phone: existingProvider.phone || '',
        profileImageUrl: existingProvider.user_profile_image_url || '',
        businessName: existingProvider.business_name || '',
        licenseNumber: existingProvider.license_number || '',
        yearsOfExperience: existingProvider.years_of_experience || 0,
        bio: existingProvider.bio || '',
        address: existingProvider.address || '',
        city: existingProvider.city || '',
        state: existingProvider.state || '',
        zipCode: existingProvider.zip_code || '',
        website: existingProvider.website || '',
        specialties: existingProvider.specialties || [],
        // Merge existing specialties, services, and treatment_categories into unified services array
        services: [
          ...(existingProvider.services || []),
          ...(existingProvider.specialties || []),
          ...(existingProvider.treatment_categories || []),
        ].filter((value, index, self) => self.indexOf(value) === index), // Remove duplicates
        treatmentCategories: existingProvider.treatment_categories || [],
        pricingRange: existingProvider.pricing_range || '',
        virtualConsultations: existingProvider.virtual_consultations || false,
        education:
          existingProvider.education?.map((edu: any) => ({
            // Handle array, string, or comma-separated string formats
            degree: Array.isArray(edu.degree)
              ? edu.degree
              : edu.degree
                ? edu.degree.includes(',')
                  ? edu.degree.split(',').map((s: string) => s.trim())
                  : [edu.degree]
                : [],
            institution: edu.institution || edu.institute || '',
            year: edu.year || '',
          })) || [],
        certifications:
          existingProvider.certifications_list?.map((cert: any) => ({
            // Handle both old (string) and new (array/comma-separated) formats
            name:
              typeof cert === 'string'
                ? [cert]
                : Array.isArray(cert.name)
                  ? cert.name
                  : cert.name
                    ? cert.name.includes(',')
                      ? cert.name.split(',').map((s: string) => s.trim())
                      : [cert.name]
                    : [],
            // Legacy string entries have no issuer or year; leave them blank
            issuer: typeof cert === 'string' ? '' : cert.issuer || '',
            year: typeof cert === 'string' ? '' : cert.year || '',
          })) || [],
        affiliations: existingProvider.affiliations || [],
        insuranceAccepted: existingProvider.insurance_accepted || [],
        languagesSpoken: existingProvider.languages_spoken || [],
      }));
    } else if (currentUser) {
      // If no provider exists, at least pre-fill user data
      setProfileData((prev) => ({
        ...prev,
        fullName: currentUser.full_name || '',
        profileImageUrl: '', // User doesn't have profile_image_url, will be set when uploaded
      }));
    }
  }, [currentUser, existingProvider]);

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (redirectTo: string = '/profile') => {
    setLoading(true);
    setError('');

    try {
      let profileImageUrl = profileData.profileImageUrl;

      // Upload profile image to Firebase Storage if a new file was selected
      if (profileData.profileImage) {
        try {
          const userId = currentUser?.id?.toString() || 'unknown';
          profileImageUrl = await uploadProfileImage(profileData.profileImage, userId);
        } catch (uploadError) {
          console.error('Failed to upload profile image:', uploadError);
          // Continue without image if upload fails
        }
      }

      // Format data for API - convert to backend expected format
      // Note: services now contains the merged list from specialties and treatment categories
      const providerData = {
        business_name: profileData.businessName,
        license_number: profileData.licenseNumber || null,
        specialties: profileData.services, // Use unified services for specialties
        bio: profileData.bio || null,
        years_of_experience: profileData.yearsOfExperience,
        address: profileData.address || null,
        city: profileData.city || null,
        state: profileData.state || null,
        zip_code: profileData.zipCode || null,
        phone: profileData.phone || null,
        website: profileData.website || null,
        // Additional fields that might need to be added to backend schema
        services: profileData.services,
        treatment_categories: profileData.services, // Use unified services for treatment categories
        pricing_range: profileData.pricingRange || null,
        // Convert education array fields to comma-separated strings for backend
        education: profileData.education.map((edu) => ({
          degree: Array.isArray(edu.degree) ? edu.degree.join(', ') : edu.degree,
          institution: edu.institution || '',
          year: edu.year || '',
        })),
        certifications_list: profileData.certifications.map((cert) => ({
          name: Array.isArray(cert.name) ? cert.name.join(', ') : cert.name,
          issuer: cert.issuer || '',
          year: cert.year || '',
        })),
        affiliations: profileData.affiliations,
        insurance_accepted: profileData.insuranceAccepted,
        languages_spoken: profileData.languagesSpoken,
      };

      // Update provider profile
      await authApi.updateProfile(providerData);

      // If we have a profile image URL, update the user profile with it
      if (profileImageUrl && profileImageUrl !== profileData.profileImageUrl) {
        try {
          await authApi.updateUserProfile({ profile_image_url: profileImageUrl });
        } catch (err) {
          console.error('Failed to update user profile image:', err);
        }
      }

      // Success! Show success state briefly then navigate
      setSuccess(true);
      setLoading(false);

      redirectTimerRef.current = setTimeout(() => {
        navigate(redirectTo);
      }, 1000);
    } catch (err: unknown) {
      // Handle validation errors from FastAPI
      let errorMessage = 'Failed to update profile';

      if (err instanceof AxiosError) {
        const e = err as AxiosError<any>;
        if (e.response?.data?.detail) {
          const detail = e.response.data.detail;
          if (Array.isArray(detail)) {
            errorMessage = detail.map((d: any) => d.msg || JSON.stringify(d)).join(', ');
          } else if (typeof detail === 'object') {
            errorMessage = JSON.stringify(detail);
          } else {
            errorMessage = detail;
          }
        }
      }

      setError(errorMessage);
      setLoading(false);
    }
  };

  const updateProfileData = (updates: Partial<ProfileData>) => {
    setProfileData((prev) => ({ ...prev, ...updates }));
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1BasicInfo data={profileData} updateData={updateProfileData} />;
      case 2:
        return <Step2Professional data={profileData} updateData={updateProfileData} />;
      case 3:
        return <Step3Services data={profileData} updateData={updateProfileData} />;
      case 4:
        return <Step4Education data={profileData} updateData={updateProfileData} />;
      case 5:
        return (
          <Step5Practice
            data={profileData}
            updateData={updateProfileData}
            googleListing={existingProvider ? { placeId: existingProvider.google_place_id } : null}
          />
        );
      case 6:
        return <Step6Review data={profileData} />;
      default:
        return null;
    }
  };

  const progressPercentage = (currentStep / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-[#eaeaea]">
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <img
              src={cloutLogo}
              alt="CLOUT"
              className="h-8 sm:h-10 lg:h-12 cursor-pointer"
              onClick={() => navigate('/dashboard')}
            />
            <h1 className="hidden sm:block font-['Ovo',_serif] text-[18px] sm:text-[20px] lg:text-[24px] text-[#1f1f1f] uppercase">
              Complete Your Profile
            </h1>
            <button
              onClick={() => handleSubmit('/dashboard')}
              disabled={loading || success}
              className="text-[#767472] hover:text-[#1f1f1f] transition-colors text-sm sm:text-base min-h-[44px] px-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save & Exit
            </button>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-[#f8f6f2] py-3 sm:py-4">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="font-['Open_Sans'] text-xs sm:text-sm text-[#767472]">
                Step {currentStep} of {totalSteps}
              </span>
              <span className="font-['Open_Sans'] text-xs sm:text-sm text-[#767472]">
                {Math.round(progressPercentage)}% Complete
              </span>
            </div>
            <div className="w-full bg-white rounded-full h-2 mb-2">
              <div
                className="bg-[#c6a664] h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <p className="font-['Open_Sans'] text-xs sm:text-sm font-semibold text-[#1f1f1f]">
              {steps[currentStep - 1]}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="max-w-4xl mx-auto">
          {error && (
            <div className="mb-4 p-3 sm:p-4 bg-red-50 border border-red-200 text-red-700 rounded text-sm sm:text-base">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 sm:p-4 bg-green-50 border border-green-200 text-green-700 rounded text-sm sm:text-base">
              ✓ Profile saved successfully! Redirecting to your profile page...
            </div>
          )}

          {renderStep()}

          {/* Navigation Buttons */}
          <div className="flex flex-col sm:flex-row justify-between mt-6 sm:mt-8 gap-3 sm:gap-0">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className={`order-2 sm:order-1 px-6 py-3 min-h-[44px] font-['Open_Sans'] text-sm font-semibold uppercase transition-colors ${
                currentStep === 1
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-transparent border border-[#1f1f1f] text-[#1f1f1f] hover:bg-[#1f1f1f] hover:text-white'
              }`}
            >
              Previous
            </button>

            {currentStep < totalSteps ? (
              <button
                onClick={handleNext}
                className="order-1 sm:order-2 px-6 py-3 min-h-[44px] bg-[#1f1f1f] text-white font-['Open_Sans'] text-sm font-semibold uppercase hover:bg-[#333] transition-colors"
              >
                Next
              </button>
            ) : (
              <button
                onClick={() => handleSubmit()}
                disabled={loading || success}
                className={`order-1 sm:order-2 px-6 py-3 min-h-[44px] text-white font-['Open_Sans'] text-sm font-semibold uppercase transition-colors disabled:opacity-50 ${
                  success ? 'bg-green-600' : 'bg-[#c6a664] hover:bg-[#b39754]'
                }`}
              >
                {loading ? 'Saving...' : success ? '✓ Saved! Redirecting...' : 'Complete Profile'}
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProfileWizard;
